import {
  FlowTriggerKind,
  type FlowTriggerResponsePayload,
  FlowTriggerVersionTarget,
} from 'flow-schema';
import { z } from 'zod';

import { type BaseItem, BaseItemSchema, type SortOrder } from '../common.js';

export enum FormEventType {
  Completion = 'Completion',
}

export const TRIGGER_KIND_LABELS: Record<FlowTriggerKind, string> = {
  [FlowTriggerKind.VoiceCall]: 'Voice Call',
  [FlowTriggerKind.JobCompletion]: 'Job Completion',
  [FlowTriggerKind.Form]: 'Form',
  [FlowTriggerKind.HL7Message]: 'HL7 Message',
  [FlowTriggerKind.X12Message]: 'X12 Message',
  [FlowTriggerKind.SftpMessage]: 'SFTP Message',
  [FlowTriggerKind.ResourceEvent]: 'Resource Event',
};

/**
 * Trigger kinds that are currently enabled in the UI.
 * Keep only these trigger kinds until we have more certainty about event emission for others.
 * Other kinds (HL7Message, X12Message, SftpMessage) are hidden pending event emission verification.
 */
export const ENABLED_TRIGGER_KINDS = [
  FlowTriggerKind.VoiceCall,
  FlowTriggerKind.JobCompletion,
  FlowTriggerKind.Form,
] as const;

export const ENABLED_TRIGGER_KIND_OPTIONS = ENABLED_TRIGGER_KINDS.map((kind) => ({
  label: TRIGGER_KIND_LABELS[kind],
  value: kind,
}));

export enum TerminalStatus {
  Success = 'Success',
  Failed = 'Failed',
}

export const BaseHl7MessageEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.HL7Message),
  messageType: z.string().nullable(),
  messageTrigger: z.string().nullable(),
  sendingFacility: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseHl7MessageEventFlowTrigger = z.infer<typeof BaseHl7MessageEventFlowTriggerSchema>;

export interface Hl7MessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseHl7MessageEventFlowTrigger {}

export const BaseX12MessageEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.X12Message),
  interchangeSenderId: z.string().nullable(),
  transactionSetIdCode: z.string().nullable(),
  transactionSetPurposeCode: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseX12MessageEventFlowTrigger = z.infer<typeof BaseX12MessageEventFlowTriggerSchema>;

export interface X12MessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseX12MessageEventFlowTrigger {}

export const BaseSftpMessageEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.SftpMessage),
  fileType: z.string().nullable(),
  flowId: z.string(),
  organizationId: z.string(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseSftpMessageEventFlowTrigger = z.infer<typeof BaseSftpMessageEventFlowTriggerSchema>;

export interface SftpMessageEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseSftpMessageEventFlowTrigger {}

export const BaseJobCompletionEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.JobCompletion),
  jobKind: z.string().nullable(),
  terminalStatus: z.enum(TerminalStatus).nullable(),
  flowId: z.string(),
  organizationId: z.string(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseJobCompletionEventFlowTrigger = z.infer<
  typeof BaseJobCompletionEventFlowTriggerSchema
>;

export interface JobCompletionEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseJobCompletionEventFlowTrigger {}

export const BaseFormEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.Form),
  type: z.enum(FormEventType),
  previsitFormTemplateIds: z.array(z.uuid()).default([]),
  flowId: z.string(),
  organizationId: z.string(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseFormEventFlowTrigger = z.infer<typeof BaseFormEventFlowTriggerSchema>;

export interface FormEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseFormEventFlowTrigger {}

/**
 * Resource event triggers are persisted in resource-gateway, not flow-service.
 * This schema describes the staff form values and the aggregator response shape.
 * It is intentionally NOT part of `BaseFlowTriggerSchema` so the flow-service
 * create endpoint cannot accept it.
 */
export const ResourceEventFlowTriggerState = z.enum(['Active', 'Inactive', 'SoftDeleted']);
export type ResourceEventFlowTriggerState = z.infer<typeof ResourceEventFlowTriggerState>;

export const BaseResourceEventFlowTriggerSchema = z.object({
  kind: z.literal(FlowTriggerKind.ResourceEvent),
  resourceKey: z.string(),
  flowId: z.string(),
  organizationId: z.string(),
  state: ResourceEventFlowTriggerState.optional(),
});
export type BaseResourceEventFlowTrigger = z.infer<typeof BaseResourceEventFlowTriggerSchema>;

export interface ResourceEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseResourceEventFlowTrigger {}

export const BaseVoiceEventFlowTriggerSchema = z.object({
  flowId: z.string(),
  kind: z.literal(FlowTriggerKind.VoiceCall),
  organizationId: z.string(),
  phoneNumber: z.string(),
  assistantId: z.string().nullable(),
  fallbackDestination: z.string().nullable().optional(),
  phoneNumberConfigurationId: z.uuid().nullable().optional(),
  versionTarget: z.enum(FlowTriggerVersionTarget),
});
export type BaseVoiceEventFlowTrigger = z.infer<typeof BaseVoiceEventFlowTriggerSchema>;

export const BaseFlowTriggerSchema = z.discriminatedUnion('kind', [
  BaseHl7MessageEventFlowTriggerSchema,
  BaseX12MessageEventFlowTriggerSchema,
  BaseSftpMessageEventFlowTriggerSchema,
  BaseVoiceEventFlowTriggerSchema,
  BaseJobCompletionEventFlowTriggerSchema,
  BaseFormEventFlowTriggerSchema,
]);
export type BaseFlowTrigger = z.infer<typeof BaseFlowTriggerSchema>;

export const SharedFlowTriggerSchema = BaseItemSchema.extend({
  kind: z.enum(FlowTriggerKind),
  flowId: z.string(),
  organizationId: z.string(),
});
export type BaseSharedFlowTrigger = z.infer<typeof SharedFlowTriggerSchema>;

export const FlowTriggerSchema = z.discriminatedUnion('kind', [
  BaseHl7MessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseX12MessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseSftpMessageEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseVoiceEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseJobCompletionEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  BaseFormEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
  // ResourceEvent is included in the read union because the aggregator response
  // surfaces these rows alongside flow-service-owned kinds. It is intentionally
  // NOT in `BaseFlowTriggerSchema` so flow-service create cannot accept it.
  BaseResourceEventFlowTriggerSchema.merge(SharedFlowTriggerSchema.omit({ kind: true })),
]);
export type FlowTrigger = z.infer<typeof FlowTriggerSchema>;

export interface VoiceEventFlowTrigger
  extends Omit<SharedFlowTrigger, 'kind'>, BaseVoiceEventFlowTrigger {}

export interface TriggerTypeMap {
  [FlowTriggerKind.HL7Message]: Hl7MessageEventFlowTrigger;
  [FlowTriggerKind.VoiceCall]: VoiceEventFlowTrigger;
  [FlowTriggerKind.X12Message]: X12MessageEventFlowTrigger;
  [FlowTriggerKind.SftpMessage]: SftpMessageEventFlowTrigger;
  [FlowTriggerKind.JobCompletion]: JobCompletionEventFlowTrigger;
  [FlowTriggerKind.Form]: FormEventFlowTrigger;
  /**
   * Resource event triggers are owned by resource-gateway. This map entry exists
   * so callers that key into TriggerTypeMap by Kind are exhaustive. Reads/writes
   * should go through `api.client.ResourceFlowTrigger`, not `api.client.Triggers`.
   */
  [FlowTriggerKind.ResourceEvent]: ResourceEventFlowTrigger;
}

/**
 * URL path segments under `/flow/triggers/` per Kind. Intentionally a
 * `Partial` because not every Kind has a flow-service-hosted route —
 * notably `ResourceEvent`, which is owned by resource-gateway. Callers
 * that key into this map must handle `undefined` (or only invoke for
 * Kinds they know are flow-service-routed).
 */
export const Paths: Partial<Record<FlowTriggerKind, string>> = {
  [FlowTriggerKind.HL7Message]: 'hl7-message-events',
  [FlowTriggerKind.VoiceCall]: 'voice-events',
  [FlowTriggerKind.X12Message]: 'x12-message-events',
  [FlowTriggerKind.SftpMessage]: 'sftp-message-events',
  [FlowTriggerKind.JobCompletion]: 'job-completion-events',
  [FlowTriggerKind.Form]: 'form-events',
};

export interface FindAllOptions {
  kind?: FlowTriggerKind;
  flowId?: string;
  organizationId?: string | string[];
  payloadType?: FlowTriggerResponsePayload;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: SortOrder;
}

export interface SharedFlowTrigger extends BaseSharedFlowTrigger, BaseItem {}
